// src/components/Registration/steps/Step3Location.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import TIP_ICON_SRC from '../../../Images/tipEligible.png';
import {
  decideTipEligible,
  normalizePlaceDetails,
  deriveCityFromObject,
  normalizeAreaKey,
} from '../../../lib/places';

const PEACH = '#EBA477';
const GREEN = '#10B981';
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const ALL_AREAS = '__ALL__';
const SEARCH_GRANULARITY_LABELS = {
  state: 'State',
  area: 'City',
  zip: 'ZIP Code',
};
const SEARCH_GRANULARITY_OPTIONS = [
  { value: 'state', label: 'State' },
  { value: 'area', label: 'City' },
  { value: 'zip', label: 'ZIP Code' },
];
const STATE_OPTIONS = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const STATE_LABEL_BY_CODE = Object.fromEntries(STATE_OPTIONS.map(([code, label]) => [code, label]));
//const TIP_ICON_SRC = (process.env.PUBLIC_URL || '') + '/tipEligible.png';

/* ---------------- helpers ---------------- */
const prettyUrl = (u = '') => {
  try {
    const url = new URL(u);
    return url.hostname.replace(/^www\./, '') + (url.pathname === '/' ? '' : url.pathname);
  } catch {
    return u || '';
  }
};

/** Final area chooser at enroll time */
function pickMarketKeyForEnrollment(detailsObj, candidateObj, searchedMk) {
  const dKey = normalizeAreaKey(detailsObj?.marketKey || '');
  const dCity = deriveCityFromObject(detailsObj || {});
  const cCity = deriveCityFromObject(candidateObj || {});
  const fallback = normalizeAreaKey(searchedMk || '');
  return dKey || dCity || cCity || fallback;
}

/* Reasons */
const TEMP_EXCLUDE_REASONS = [
  'Remodel / Construction',
  'Not Yet Open',
  'Permit / License Pending',
  'Relocation In Progress',
  'Seasonal (Pre-season)',
  'Duplicate Listing',
  'Not My Store',
];

/* ------------ frosted controls ------------ */
const FrostedInput = ({ style, ...props }) => (
  <input
    className="frosted-input"
    {...props}
    style={{
      width: '100%',
      height: 60,
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 18,
      padding: '0 18px',
      outline: 'none',
      boxSizing: 'border-box',
      maxWidth: '100%',
      ...style,
    }}
  />
);

const SmallButton = ({ children, style, ...props }) => (
  <button
    {...props}
    style={{
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.18)',
      color: 'white',
      borderRadius: 12,
      padding: '8px 12px',
      cursor: 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

const ActionButton = ({ children, style, ...props }) => (
  <button
    {...props}
    style={{
      background: PEACH,
      border: `1px solid ${PEACH}`,
      color: '#1a1a1a',
      borderRadius: 12,
      padding: '10px 14px',
      fontWeight: 600,
      cursor: 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

const TipEligibleIcon = ({ size = 16, style }) => (
  <img
    src={TIP_ICON_SRC}
    alt="Tip eligible"
    title="Tip eligible"
    style={{ width: size, height: size, verticalAlign: 'middle', display: 'inline-block', ...style }}
    onError={(e) => {
      e.currentTarget.style.display = 'none';
    }}
  />
);

/* ---------- NEW: market coercion helper (fixes [object Object]) ---------- */
const coerceMarketToString = (m) =>
  typeof m === 'string' ? m : (m?.marketKey || m?.name || '');

const normalizeSearchMode = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return ['state', 'area', 'zip'].includes(raw) ? raw : '';
};

const normalizeSearchScopeValue = (value, mode) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (mode === 'state') {
    const upper = raw.toUpperCase();
    return /^[A-Z]{2}$/.test(upper) ? upper : '';
  }
  if (mode === 'zip') {
    const zip = raw.replace(/\D+/g, '').slice(0, 5);
    return /^\d{5}$/.test(zip) ? zip : '';
  }
  return normalizeAreaKey(raw);
};

const computeLegacyKey = (einTaxId, idx) => {
  const clean = String(einTaxId || '').trim();
  return clean ? `EIN:${clean}` : `IDX:${idx}`;
};

const ensureMarketObject = (m) => {
  if (m && typeof m === 'object') {
    const mk = coerceMarketToString(m);
    return mk ? { marketKey: mk, name: mk } : null;
  }
  const mk = coerceMarketToString(m);
  return mk ? { marketKey: mk, name: mk } : null;
};

const flattenEinFormsToBusinesses = (forms = []) => {
  const out = [];
  (Array.isArray(forms) ? forms : []).forEach((form, einIdx) => {
    if (!form) return;
    const brands = Array.isArray(form.brands) && form.brands.length ? form.brands : [form];
    brands.forEach((brand, brandIdx) => {
      if (!brand) return;
      const marketsRaw = Array.isArray(brand.markets) && brand.markets.length
        ? brand.markets
        : Array.isArray(brand?.marketsInput?.regionalList)
          ? brand.marketsInput.regionalList
          : [];
      const markets = marketsRaw.map(ensureMarketObject).filter(Boolean);

      const displayName =
        typeof brand?.brandName === 'string' && brand.brandName.trim()
          ? brand.brandName.trim()
          : typeof brand?.name === 'string'
          ? brand.name.trim()
          : '';

      out.push({
        legalEntityName: form.legalBusinessName || form.legalEntityName || '',
        ein: form.einTaxId || form.ein || '',
        ownershipType: form.ownershipType || '',
        primaryContact: form.primaryContact || { name: '', email: '', phone: '' },
        aliases: Array.isArray(form.aliases) ? form.aliases : [],
        dbaName: displayName,
        category: brand.businessCategory || brand.category || '',
        baseContributionPct: Number(brand.baselineContribution || 2),
        tagline: brand.tagline || '',
        description: brand.description || '',
        logoUrl: brand.logoUrl || '',
        isOnlineOnly: !!brand.isOnlineOnly,
        primaryWebsiteUrl: brand.primaryWebsiteUrl || '',
        locationSearch: {
          mode: normalizeSearchMode(brand?.locationSearch?.mode),
          values: Array.isArray(brand?.locationSearch?.values) ? brand.locationSearch.values.map((v) => String(v || '').trim()).filter(Boolean) : [],
          searchName: String(brand?.locationSearch?.searchName || displayName || '').trim(),
        },
        markets,
        tempBusinessKey: brand.tempBusinessKey || form.tempBusinessKey || `${einIdx}-${brandIdx}`,
        footprint: form.footprint || '',
      });
    });
  });
  return out;
};

/* ===================== Component ===================== */
export default function Step3Location({
  data = {},
  step2Data = {},
  onNext,
  onPrevious,
  loading,
  isFirstStep,
  onBindNext,
}) {
  const step3Data = useMemo(() => (data?.step3View ? { ...data, ...data.step3View } : data || {}), [data]);
  const step2EinForms = step2Data?.einForms;
  /* ---------- Build per-brand contexts from Step 2 ---------- */
  const seedAreasForEin = (entry) => {
    const locationSearchValues = Array.isArray(entry?.locationSearch?.values)
      ? entry.locationSearch.values.map((value) => String(value || ''))
      : [];
    let arr = [];

    if (locationSearchValues.length) {
      arr = locationSearchValues;
    } else if (Array.isArray(entry?.markets) && entry.markets.length) {
      arr = entry.markets.map(coerceMarketToString);
    } else if (Array.isArray(entry?.marketsInput?.regionalList)) {
      arr = entry.marketsInput.regionalList.map((s) => String(s || ''));
    }

    // Normalize → Title Case, drop empties, dedupe, and cap at 10
    const areas = Array.from(
      new Set(
        arr
          .map((s) => normalizeAreaKey(String(s || '').trim()))
          .filter(Boolean)
      )
    ).slice(0, 10);

    return areas;
  };

  const seedTipEligible = (f) => {
    if (typeof f.tipEligible === 'boolean') return f.tipEligible;
    if (typeof f?.tipPolicy?.defaultEligible === 'boolean') return f.tipPolicy.defaultEligible;
    return null; // tri-state
  };

  const initialEinContexts = useMemo(() => {
    const rawBusinesses =
      (Array.isArray(step2Data.businesses) && step2Data.businesses.length
        ? step2Data.businesses
        : flattenEinFormsToBusinesses(step2Data.einForms || [])) || [];

    if (!rawBusinesses.length) return [];

    const storeByBusiness =
      step3Data.storeLocationsByBusinessKey ||
      step3Data.storeLocationsByEin ||
      {};

    const arr = rawBusinesses.map((biz, idx) => {
      const key = String(biz?.tempBusinessKey || computeLegacyKey(biz?.ein, idx));
      const legacyKey = computeLegacyKey(biz?.ein, idx);

      const labelParts = [];
      if ((biz?.dbaName || '').trim()) labelParts.push(String(biz.dbaName).trim());
      if ((biz?.legalEntityName || '').trim() && biz.legalEntityName !== biz.dbaName) {
        labelParts.push(String(biz.legalEntityName).trim());
      }
      const label = labelParts.join(' • ') || `Brand ${idx + 1}`;

      const areas = seedAreasForEin(biz);
      const activeArea = (areas[0] || '').trim();

      const storeLocations =
        (Array.isArray(storeByBusiness?.[key]) ? storeByBusiness[key] : null) ||
        (Array.isArray(storeByBusiness?.[legacyKey]) ? storeByBusiness[legacyKey] : []);

      const searchDefault = (biz.dbaName || biz.legalEntityName || '').trim();
      const searchMode = normalizeSearchMode(biz?.locationSearch?.mode) || (areas.length ? 'area' : '');
      const searchName = String(biz?.locationSearch?.searchName || searchDefault).trim();
      const makeAreaState = () => ({
        placesResults: [],
        candidateMap: {},
        dispositions: {},
        isPlacesLoading: false,
        placesError: '',
        discoveredIds: [],
        isApplying: false,
        manualOpen: false,
        manual: { name: '', addressText: '', phone: '', website: '' },
        _autoSearched: false,
        _reviewed: false,
      });

      const areasState = {};
      areas.forEach((a) => {
        areasState[String(a)] = makeAreaState();
      });

      (Array.isArray(storeLocations) ? storeLocations : []).forEach((loc) => {
        const mk = String(loc?.marketKey || '').trim();
        if (mk && areasState[mk]) {
          areasState[mk]._reviewed = true;
        }
      });

      return {
        key: String(key),
        legacyKey,
        tempBusinessKey: String(key),
        einTaxId: String(biz?.ein || '').trim(),
        label,
        ownershipType: biz?.ownershipType || '',
        isOnlineOnly: !!biz?.isOnlineOnly,
        tipEligible: seedTipEligible(biz),
        searchMode,
        searchName,
        availableAreas: areas,
        activeArea: activeArea || '',
        areasState,
        storeLocations: Array.isArray(storeLocations) ? storeLocations : [],
        searchNameDefault: searchDefault,
      };
    });

    if (
      arr.length === 1 &&
      !Object.keys(storeByBusiness || {}).length &&
      Array.isArray(step3Data.storeLocations)
    ) {
      arr[0].storeLocations = step3Data.storeLocations;
    }

    return arr;
  }, [
    step2Data.businesses,
    step2Data.einForms,
    step3Data.storeLocationsByBusinessKey,
    step3Data.storeLocationsByEin,
    step3Data.storeLocations,
  ]);

  const [einContexts, setEinContexts] = useState(initialEinContexts);
  const [activeKey, setActiveKey] = useState(String(initialEinContexts[0]?.key || ''));
  const activeIdx = Math.max(0, einContexts.findIndex((c) => String(c.key) === String(activeKey)));
  const active = useMemo(() => einContexts[activeIdx] || {}, [einContexts, activeIdx]);

  // Keep a live ref for cross-context preload logic
  const einAllRef = useRef(einContexts);
  useEffect(() => {
    einAllRef.current = einContexts;
  }, [einContexts]);

  const updateActive = useCallback(
    (patch) => {
      setEinContexts((prev) => prev.map((c, i) => (i === activeIdx ? { ...c, ...patch } : c)));
    },
    [activeIdx]
  );

  // area state helpers (active)
  const getAreaState = (ctx, mk) => {
    const k = String(mk || ctx.activeArea || '');
    return (ctx.areasState && ctx.areasState[k]) || null;
  };

  const setAreaState = useCallback(
    (mk, patch) => {
      setEinContexts((prev) =>
        prev.map((c, i) => {
          if (i !== activeIdx) return c;
          const key = String(mk || c.activeArea || '');
          const area = c.areasState[key] || {};
          const newPatch = typeof patch === 'function' ? patch(area) : patch;
          return { ...c, areasState: { ...c.areasState, [key]: { ...area, ...newPatch } } };
        })
      );
    },
    [activeIdx]
  );

  // NEW: generic setters for any EIN index (needed for preloading non-active EINs)
  const setAreaStateAt = useCallback((ctxIdx, mk, patch) => {
    setEinContexts((prev) =>
      prev.map((c, i) => {
        if (i !== ctxIdx) return c;
        const key = String(mk || c.activeArea || '');
        const area = c.areasState[key] || {};
        const newPatch = typeof patch === 'function' ? patch(area) : patch;
        return { ...c, areasState: { ...c.areasState, [key]: { ...area, ...newPatch } } };
      })
    );
  }, []);

  /* ---------- Auto-add chips if locations carry new areas ---------- */
  useEffect(() => {
    if (!active || !active.storeLocations) return;

    const locAreas = Array.from(new Set(active.storeLocations.map((l) => String(l.marketKey || '')).filter(Boolean)));
    const existing = new Set((active.availableAreas || []).map(String));
    const next = [...(active.availableAreas || [])];

    let changed = false;
    for (const mk of locAreas) {
      if (!existing.has(mk)) {
        next.push(mk);
        changed = true;
        if (!active.areasState?.[mk]) {
          setEinContexts((prev) =>
            prev.map((c, i) => {
              if (i !== activeIdx) return c;
              return {
                ...c,
                areasState: {
                  ...c.areasState,
                  [mk]: {
                    placesResults: [],
                    candidateMap: {},
                    dispositions: {},
                    isPlacesLoading: false,
                    placesError: '',
                    discoveredIds: [],
                    isApplying: false,
                    manualOpen: false,
                    manual: { name: '', addressText: '', phone: '', website: '' },
                    _autoSearched: false,
                    _reviewed: (c.storeLocations || []).some((l) => String(l.marketKey || '') === mk),
                  },
                },
              };
            })
          );
        }
      }
    }
    if (changed) updateActive({ availableAreas: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.storeLocations]); // intentionally scoped

  /* ---------- Auto-search once when entering an area (active EIN only) ---------- */
  useEffect(() => {
    if (!active || active.isOnlineOnly) return;
    const mk = active.activeArea;
    if (!mk || mk === ALL_AREAS) return;
    const astate = getAreaState(active, mk);
    if (!astate || astate._autoSearched) return;

    const name = (active.searchName ?? active.searchNameDefault ?? '').trim();
    const hasQuery = !!name;

    if (hasQuery && (astate.placesResults || []).length === 0 && !astate.isPlacesLoading) {
      setAreaState(mk, { _autoSearched: true });
      (async () => {
        await performPlacesSearch(true, mk, name);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.activeArea]); // intentionally scoped

  /* ---------- Area and visible locations ---------- */
  const area = useMemo(() => {
    if (!active || !active.activeArea || active.activeArea === ALL_AREAS) return {};
    return getAreaState(active, active.activeArea) || {};
  }, [active]);

  const visibleCandidates = useMemo(() => {
    const addedPlaceIds = new Set((active.storeLocations || []).map((loc) => String(loc.placeId || '')).filter(Boolean));
    return (area.placesResults || []).filter((candidate) => !addedPlaceIds.has(String(candidate.place_id || '')));
  }, [active.storeLocations, area.placesResults]);

  const visibleLocs = useMemo(() => {
    const all = active.storeLocations || [];
    if (!active.activeArea || active.activeArea === ALL_AREAS) {
      return all.map((loc, i) => ({ loc, globalIdx: i }));
    }
    const mk = String(active.activeArea || '');
    return all
      .map((loc, i) => ({ loc, globalIdx: i }))
      .filter((x) => String(x.loc.marketKey || '') === mk);
  }, [active]);

  /* ---------- Coverage helpers ---------- */
  const calcCoverage = useCallback((ctx, mk) => {
    if (mk && mk !== ALL_AREAS) {
      const astate = (ctx.areasState || {})[mk] || {};
      const discovered = new Set((astate.discoveredIds || []).filter(Boolean));
      const notMine = new Set(
        Object.entries(astate.dispositions || {})
          .filter(([, v]) => v?.action === 'not_mine')
          .map(([id]) => id)
      );
      const expected = Math.max(0, discovered.size - notMine.size);
      const enrolled = (ctx.storeLocations || []).filter((l) => String(l.marketKey || '') === String(mk)).length;
      const pct = expected > 0 ? Math.round((enrolled / expected) * 100) : enrolled > 0 ? 100 : 0;
      return { expectedCount: expected, enrolledCount: enrolled, coveragePct: pct };
    }

    let expectedTotal = 0;
    let enrolledTotal = 0;
    (ctx.availableAreas || []).forEach((a) => {
      const c = calcCoverage(ctx, a);
      expectedTotal += c.expectedCount;
      enrolledTotal += c.enrolledCount;
    });
    const pct = expectedTotal > 0 ? Math.round((enrolledTotal / expectedTotal) * 100) : enrolledTotal > 0 ? 100 : 0;
    return { expectedCount: expectedTotal, enrolledCount: enrolledTotal, coveragePct: pct };
  }, []);

  const areaSummary = useMemo(() => {
    const byArea = {};
    (active.availableAreas || []).forEach((mk) => {
      const { enrolledCount } = calcCoverage(active, mk);
      byArea[mk] = { enrolled: enrolledCount };
    });
    return byArea;
  }, [active, calcCoverage]);

  const unmetRequiredIds = useMemo(() => {
    if (!active.activeArea || active.activeArea === ALL_AREAS) return [];
    const astate = area || {};
    const disp = astate.dispositions || {};
    const ids = (astate.discoveredIds || []).filter(Boolean).filter((id) => disp[id]?.action !== 'not_mine');
    const present = new Set(
      (active.storeLocations || [])
        .filter((l) => String(l.marketKey || '') === String(active.activeArea || ''))
        .map((l) => l.placeId)
        .filter(Boolean)
    );
    return ids.filter((id) => !present.has(id));
  }, [active, area]);

  /* ---------- Backend proxy calls (ACTIVE EIN) ---------- */
  const performPlacesSearch = useCallback(
    async (suppressErrorBanner = false, overrideMk, overrideName, overrideCity, overridePhone) => {
      const mk = overrideMk || active.activeArea;
      if (!mk || mk === ALL_AREAS) {
        if (!suppressErrorBanner) setAreaState(active.activeArea, { placesError: 'Choose a search area first.' });
        return;
      }

      const an = (overrideName ?? active.searchName ?? active.searchNameDefault ?? '').trim();
      const ac = String(overrideCity || mk);
      const ap = (overridePhone ?? '').trim();

      if (!an && !ap) {
        if (!suppressErrorBanner) setAreaState(mk, { placesError: 'Provide business name or phone to search.' });
        return;
      }

      setAreaState(mk, { _reviewed: true });
      setAreaState(mk, { placesError: '', placesResults: [], candidateMap: {}, isPlacesLoading: true });

      try {
        const params = new URLSearchParams();
        if (an) params.set('name', an);
        if (ac) params.set('city', ac);
        if (ap) params.set('phone', ap);

        const res = await fetch(`${API_BASE}/api/places/search?${params.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || json.message || 'Search failed');

        const list = json.candidates || json.results || json.places || [];
        const candidates = list
          .map((r) => {
            const place_id = r.place_id || r.placeId;
            const displayName = r.displayName || r.name;
            const formatted_address = r.formatted_address || r.formattedAddress || r.vicinity || r.address;
            return {
              place_id,
              displayName,
              formatted_address,
              primaryType: r.primaryType || '',
              iconMaskBaseUri: r.iconMaskBaseUri || '',
              iconBackgroundColor: r.iconBackgroundColor || '',
              businessStatus: r.businessStatus || '',
            };
          })
          .filter((c) => c.place_id && c.displayName);

        // Hide already-enrolled for this EIN (any area)
        const enrolledIds = new Set((active.storeLocations || []).map((l) => l.placeId).filter(Boolean));
        const filtered = candidates.filter((c) => !enrolledIds.has(c.place_id));

        const map = {};
        for (const c of filtered) map[c.place_id] = c;

        const ids = filtered.map((c) => c.place_id).filter(Boolean);

        setAreaState(mk, (prevAreaState) => {
          const prevIds = prevAreaState.discoveredIds || [];
          const mergedIds = Array.from(new Set([...prevIds, ...ids]));
          return {
            candidateMap: map,
            placesResults: filtered.slice(0, 50),
            discoveredIds: mergedIds,
          };
        });
      } catch (e) {
        if (!suppressErrorBanner) setAreaState(mk, { placesError: e.message || 'Search failed. Please try again.' });
      } finally {
        setAreaState(mk, { isPlacesLoading: false });
      }
    },
    [active, setAreaState]
  );

  // NEW: Cross-EIN search (used by preload for non-active EINs)
  const performPlacesSearchAt = useCallback(
    async (ctxIdx, suppressErrorBanner = true, overrideMk, overrideName, overrideCity, overridePhone) => {
      const ctx = einAllRef.current?.[ctxIdx];
      if (!ctx) return;

      const mk = overrideMk || ctx.activeArea;
      if (!mk || mk === ALL_AREAS) {
        if (!suppressErrorBanner) setAreaStateAt(ctxIdx, ctx.activeArea, { placesError: 'Choose a search area first.' });
        return;
      }

      const an = (overrideName ?? ctx.searchName ?? ctx.searchNameDefault ?? '').trim();
      const ac = String(overrideCity || mk);
      const ap = (overridePhone ?? '').trim();

      if (!an && !ap) {
        if (!suppressErrorBanner) setAreaStateAt(ctxIdx, mk, { placesError: 'Provide business name or phone to search.' });
        return;
      }

      setAreaStateAt(ctxIdx, mk, { _reviewed: true });
      setAreaStateAt(ctxIdx, mk, { placesError: '', placesResults: [], candidateMap: {}, isPlacesLoading: true });

      try {
        const params = new URLSearchParams();
        if (an) params.set('name', an);
        if (ac) params.set('city', ac);
        if (ap) params.set('phone', ap);

        const res = await fetch(`${API_BASE}/api/places/search?${params.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || json.message || 'Search failed');

        const list = json.candidates || json.results || json.places || [];
        const candidates = list
          .map((r) => {
            const place_id = r.place_id || r.placeId;
            const displayName = r.displayName || r.name;
            const formatted_address = r.formatted_address || r.formattedAddress || r.vicinity || r.address;
            return {
              place_id,
              displayName,
              formatted_address,
              primaryType: r.primaryType || '',
              iconMaskBaseUri: r.iconMaskBaseUri || '',
              iconBackgroundColor: r.iconBackgroundColor || '',
              businessStatus: r.businessStatus || '',
            };
          })
          .filter((c) => c.place_id && c.displayName);

        const enrolledIds = new Set((ctx.storeLocations || []).map((l) => l.placeId).filter(Boolean));
        const filtered = candidates.filter((c) => !enrolledIds.has(c.place_id));

        const map = {};
        for (const c of filtered) map[c.place_id] = c;
        const ids = filtered.map((c) => c.place_id).filter(Boolean);

        setAreaStateAt(ctxIdx, mk, (prevAreaState) => {
          const prevIds = prevAreaState.discoveredIds || [];
          const mergedIds = Array.from(new Set([...prevIds, ...ids]));
          return {
            candidateMap: map,
            placesResults: filtered.slice(0, 50),
            discoveredIds: mergedIds,
          };
        });
      } catch (e) {
        if (!suppressErrorBanner) setAreaStateAt(ctxIdx, mk, { placesError: e.message || 'Search failed. Please try again.' });
      } finally {
        setAreaStateAt(ctxIdx, mk, { isPlacesLoading: false });
      }
    },
    [setAreaStateAt]
  );

  const fetchPlaceDetails = async (placeId, mk) => {
    try {
      const res = await fetch(`${API_BASE}/api/places/details?placeId=${encodeURIComponent(placeId)}`, {
        credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || d.message || 'Details failed');
      return normalizePlaceDetails(d, mk);
    } catch (e) {
      setAreaState(mk, { placesError: e.message || 'Failed to load details.' });
      return null;
    }
  };

  // Remove an area chip if it has 0 enrolled and 0 expected after 'not_mine'
  function pruneAreaIfEmpty(ctx) {
    const next = { ...ctx };
    const areas = Array.isArray(ctx.availableAreas) ? [...ctx.availableAreas] : [];
    const pruned = [];
    for (const mk of areas) {
      const astate = (ctx.areasState || {})[mk] || {};
      const discovered = new Set((astate.discoveredIds || []).filter(Boolean));
      const notMine = new Set(
        Object.entries(astate.dispositions || {})
          .filter(([, v]) => v?.action === 'not_mine')
          .map(([id]) => id)
      );
      const expected = Math.max(0, discovered.size - notMine.size);
      const enrolled = (ctx.storeLocations || []).filter((l) => String(l.marketKey || '') === String(mk)).length;
      if (enrolled === 0 && expected === 0) {
        pruned.push(mk);
      }
    }
    if (pruned.length) {
      next.availableAreas = areas.filter((a) => !pruned.includes(a));
      const as = { ...(ctx.areasState || {}) };
      pruned.forEach((p) => {
        delete as[p];
      });
      next.areasState = as;
      if (pruned.includes(ctx.activeArea)) {
        next.activeArea = next.availableAreas[0] || ALL_AREAS;
      }
    }
    return next;
  }

  /**
   * Apply dispositions:
   * - Enroll/Exclude fetches details, then **assigns area** using details OR candidate OR search area.
   * - Sets tipEligibility per location.
   * - Preserves 'not_mine'.
   * - Prunes empty areas after actions (Wilmette drops when empty).
   */
  const applyDispositions = async () => {
    const mk = active.activeArea;
    if (!mk || mk === ALL_AREAS) return;

    const currentAreaState = getAreaState(active, mk) || {};
    const disp = currentAreaState.dispositions || {};
    const ids = Object.keys(disp);
    if (!ids.length) return;

    setAreaState(mk, { isApplying: true, _reviewed: true });

    try {
      const targetIds = ids.filter((id) => ['enroll', 'exclude'].includes(disp[id]?.action));
      const detailsList = await Promise.all(targetIds.map((pid) => fetchPlaceDetails(pid, mk)));

      const combos = targetIds.map((pid, i) => ({
        pid,
        details: detailsList[i],
        candidate: currentAreaState.candidateMap?.[pid] || {},
      }));

      const nextLocs = [...(active.storeLocations || [])];
      const existingKeys = new Set(nextLocs.map((l) => `${l.placeId}|${l.marketKey || ''}`));

      combos.forEach(({ pid, details, candidate }) => {
        if (!details) return;
        const action = disp[pid]?.action;
        const reason = disp[pid]?.reason || '';
        const reopenDate = disp[pid]?.reopenDate || '';

        // FINAL area decision
        const effectiveMarketKey = pickMarketKeyForEnrollment(details, candidate, mk);

        const tipEligible = decideTipEligible({ mcc: details.mcc, primaryType: details.primaryType }, active.tipEligible);

        const base = {
          ...details,
          status: action === 'exclude' ? 'temp_excluded' : 'active',
          exclusionReason: action === 'exclude' ? reason : '',
          reopenDate: action === 'exclude' ? reopenDate : '',
          marketKey: effectiveMarketKey,
          tipEligible,
          einTaxId: active.einTaxId || '',
        };

        const key = `${details.placeId}|${effectiveMarketKey}`;
        if (!existingKeys.has(key)) {
          nextLocs.push(base);
          existingKeys.add(key);
        } else {
          const idx = nextLocs.findIndex((l) => `${l.placeId}|${l.marketKey || ''}` === key);
          if (idx >= 0) nextLocs[idx] = { ...nextLocs[idx], ...base };
        }
      });

      updateActive({ storeLocations: nextLocs });

      // Clear applied enroll/exclude; keep 'not_mine'
      setAreaState(mk, (prevAreaState) => {
        const old = prevAreaState.dispositions || {};
        const keep = {};
        for (const [id, d] of Object.entries(old)) {
          if (d?.action === 'not_mine') keep[id] = d;
        }
        return { dispositions: keep };
      });

      // PRUNE empty areas after applying actions
      setEinContexts((prev) => prev.map((c, i) => (i === activeIdx ? pruneAreaIfEmpty(c) : c)));
    } finally {
      setAreaState(mk, { isApplying: false });
    }
  };

  /* ---------- Manual add (area-scoped) ---------- */
  const addManualLocation = () => {
    const mk = active.activeArea;
    if (!mk || mk === ALL_AREAS) return;

    const currentArea = getAreaState(active, mk) || {};
    const nm = (currentArea.manual?.name || '').trim();
    const addr = (currentArea.manual?.addressText || '').trim();
    if (!nm || !addr) return;

    const tipEligible = decideTipEligible({ mcc: '', primaryType: '' }, active.tipEligible);

    const loc = {
      placeId: '',
      placeSource: 'manual',
      name: nm,
      addressText: addr,
      phone: currentArea.manual?.phone?.trim(),
      website: currentArea.manual?.website?.trim(),
      hoursText: [],
      status: 'active',
      exclusionReason: '',
      reopenDate: '',
      marketKey: mk,
      primaryType: '',
      iconMaskBaseUri: '',
      iconBackgroundColor: '',
      businessStatus: '',
      tipEligible,
      einTaxId: active.einTaxId || '',
    };

    updateActive({ storeLocations: [...(active.storeLocations || []), loc] });
    setAreaState(mk, { manual: { name: '', addressText: '', phone: '', website: '' }, _reviewed: true });
  };

  /* ---------- Resolution gating ---------- */
  function isAreaFullyResolved(ctx, mk) {
    const astate = (ctx.areasState || {})[mk] || {};
    const disp = astate.dispositions || {};

    const discovered = (astate.discoveredIds || []).filter(Boolean);
    const required = discovered.filter((id) => disp[id]?.action !== 'not_mine');

    const present = new Set(
      (ctx.storeLocations || [])
        .filter((l) => String(l.marketKey || '') === String(mk))
        .map((l) => l.placeId)
        .filter(Boolean)
    );

    const unresolvedIds = required.filter((id) => !present.has(id));
    const hasPendingActions = Object.values(disp).some((d) => d && (d.action === 'enroll' || d.action === 'exclude'));

    const excludedInvalid = (ctx.storeLocations || []).some(
      (l) =>
        String(l.marketKey || '') === String(mk) &&
        l.status === 'temp_excluded' &&
        (!(l.exclusionReason || '').trim() || !(l.reopenDate || '').trim())
    );

    return unresolvedIds.length === 0 && !hasPendingActions && !excludedInvalid;
  }

  function areaWasReviewedOrHasData(ctx, mk) {
    const astate = (ctx.areasState || {})[mk] || {};
    const hasAnyLoc = (ctx.storeLocations || []).some((l) => String(l.marketKey || '') === String(mk));
    return !!(astate._reviewed || (astate.discoveredIds || []).length || hasAnyLoc);
  }

  const allAreasOk = einContexts.every((c) => (c.isOnlineOnly ? true : (c.availableAreas || []).length > 0));

  const allAreasReviewed = einContexts.every((c) => {
    if (c.isOnlineOnly) return true;
    const areas = c.availableAreas || [];
    return areas.every((mk) => areaWasReviewedOrHasData(c, mk));
  });

  const allAreasResolved = einContexts.every((c) => {
    if (c.isOnlineOnly) return true;
    const areas = c.availableAreas || [];
    return areas.every((mk) => isAreaFullyResolved(c, mk));
  });

  const canContinue = allAreasOk && allAreasReviewed && allAreasResolved && !loading;

  // --- Stable binding to parent ---
  const onNextRef = useRef(onNext);
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  const einRef = useRef(einContexts);
  useEffect(() => {
    einRef.current = einContexts;
  }, [einContexts]);

  const canContinueRef = useRef(canContinue);
  useEffect(() => {
    canContinueRef.current = canContinue;
  }, [canContinue]);

  const buildStep3Payload = useCallback((all) => {
    const updates = [];
    const storeLocationsByKey = {};
    const marketsByKey = {};
    const flattened = [];
    let allDuplicates = new Set();

    all.forEach((ctx, idx) => {
      const markets = (ctx.availableAreas || []).map((mk) => {
        const c = (ctx.storeLocations || []).filter((l) => String(l.marketKey || '') === String(mk));
        const astate = (ctx.areasState || {})[mk] || {};
        const discoveredSet = new Set((astate.discoveredIds || []).filter(Boolean));
        const notMine = new Set(
          Object.entries(astate.dispositions || {})
            .filter(([, v]) => v?.action === 'not_mine')
            .map(([id]) => id)
        );
        const expected = Math.max(0, discoveredSet.size - notMine.size);
        const enrolled = c.length;

        return {
          marketKey: mk,
          name: mk,
          marketType: 'operating_area',
          attested: true,
          coverage: { expectedCount: expected, enrolledCount: enrolled },
        };
      });

      const seen = new Set();
      const dups = new Set();
      const locsOut = (ctx.storeLocations || []).map((s) => {
        const pid = s.placeId || '';
        const mk = s.marketKey || '';
        const key = `${pid}|${mk}`;
        if (pid) {
          if (seen.has(key)) dups.add(key);
          else seen.add(key);
        }
        return {
          placeId: pid,
          placeSource: s.placeSource || (pid ? 'google' : 'manual'),
          name: s.name || '',
          addressText: s.addressText || '',
          phone: s.phone || '',
          website: s.website || '',
          hoursText: Array.isArray(s.hoursText) ? s.hoursText : [],
          primaryType: s.primaryType || '',
          iconMaskBaseUri: s.iconMaskBaseUri || '',
          iconBackgroundColor: s.iconBackgroundColor || '',
          businessStatus: s.businessStatus || '',
          status: s.status === 'temp_excluded' ? 'temp_excluded' : 'active',
          exclusionReason: s.exclusionReason || '',
          reopenDate: s.reopenDate || '',
          marketKey: mk,
          einTaxId: s.einTaxId || ctx.einTaxId || '',
          latitude: typeof s.latitude === 'number' ? s.latitude : undefined,
          longitude: typeof s.longitude === 'number' ? s.longitude : undefined,
          mcc: s.mcc || '',
          tipEligible: !!s.tipEligible,
          businessKey: ctx.key,
          tempBusinessKey: ctx.tempBusinessKey || ctx.key,
        };
      });

      allDuplicates = new Set([...allDuplicates, ...dups]);

      updates.push({
        index: idx,
        businessKey: ctx.key,
        storeLocations: locsOut,
        markets,
        locationSearch: {
          mode: ctx.searchMode || '',
          values: ctx.availableAreas || [],
          searchName: ctx.searchName || ctx.searchNameDefault || '',
        },
        opsReviewQueue: dups.size > 0,
        duplicatePlaceIds: Array.from(dups),
      });

      storeLocationsByKey[ctx.key] = locsOut;
      marketsByKey[ctx.key] = markets;
      flattened.push(...locsOut);
    });

    const firstMarketKey = all.find((c) => (c.storeLocations || []).length > 0)?.availableAreas?.[0] || '';

    const payload = {
      updates,
      step2Patch: {
        einForms: all.map((ctx, idx) => {
          const source = (Array.isArray(step2EinForms) ? step2EinForms : [])[idx];
          if (!source) return source;
          const brands = Array.isArray(source.brands) ? source.brands : [];
          return {
            ...source,
            brands: brands.map((brand, brandIdx) => {
              const matchesKey = String(brand?.tempBusinessKey || `${idx}-${brandIdx}`) === String(ctx.tempBusinessKey || ctx.key);
              if (!matchesKey) return brand;
              return {
                ...brand,
                locationSearch: {
                  mode: ctx.searchMode || '',
                  values: ctx.availableAreas || [],
                  searchName: ctx.searchName || ctx.searchNameDefault || '',
                  pendingInput: '',
                },
              };
            }),
          };
        }),
      },
      step3View: {
        storeLocationsByBusinessKey: storeLocationsByKey,
        storeLocationsByEin: storeLocationsByKey,
        marketsByBusinessKey: marketsByKey,
        marketsByEin: marketsByKey,
        locationSearchByBusinessKey: Object.fromEntries(
          all.map((ctx) => [
            ctx.key,
            {
              mode: ctx.searchMode || '',
              values: ctx.availableAreas || [],
              searchName: ctx.searchName || ctx.searchNameDefault || '',
            },
          ])
        ),
        storeLocations: flattened,
        marketKey: firstMarketKey, // back-compat
        opsReviewQueue: allDuplicates.size > 0,
        duplicatePlaceIds: Array.from(allDuplicates),
      },
    };

    return payload;
  }, [step2EinForms]);

  const handleClick = useCallback(async () => {
    if (!canContinueRef.current) return;

    const all = einRef.current;
    const payload = buildStep3Payload(all);

    await onNextRef.current?.(payload);
  }, [buildStep3Payload]);

  useEffect(() => {
    if (!onBindNext) return;
    onBindNext({ onClick: handleClick, disabled: !canContinue || loading });
    return () => onBindNext({ onClick: null, disabled: true });
  }, [onBindNext, handleClick, canContinue, loading]);

  /* ---------- Preload search for *every* EIN and *every* area once ---------- */
  useEffect(() => {
    einContexts.forEach((ctx, ci) => {
      if (ctx.isOnlineOnly) return;
      (ctx.availableAreas || []).forEach((mk) => {
        if (ci === activeIdx && String(mk) === String(active.activeArea || '')) return;
        const astate = (ctx.areasState || {})[mk] || {};
        if (astate._autoSearched) return;

        const name = (ctx.searchName ?? ctx.searchNameDefault ?? '').trim();
        const hasQuery = !!name;

        if (hasQuery && (astate.placesResults || []).length === 0 && !astate.isPlacesLoading) {
          setAreaStateAt(ci, mk, { _autoSearched: true });
          // suppress banner for preloads
          performPlacesSearchAt(ci, true, mk, name);
        }
      });
    });
  }, [einContexts, setAreaStateAt, performPlacesSearchAt, activeIdx, active.activeArea]);

  /* ---------------- UI ---------------- */
  const [newAreaOpen, setNewAreaOpen] = useState(false);
  const [newAreaText, setNewAreaText] = useState('');
  const [newStateSelections, setNewStateSelections] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [pendingRemovalArea, setPendingRemovalArea] = useState(null);

  const addArea = useCallback(() => {
    const v = normalizeSearchScopeValue(newAreaText, active.searchMode);
    if (!v) return;

    const exists = (active.availableAreas || []).some((a) => normalizeAreaKey(a) === v);
    if (exists) {
      updateActive({ activeArea: v });
      setNewAreaOpen(false);
      setNewAreaText('');
      return;
    }

    setEinContexts((prev) =>
      prev.map((c, i) => {
        if (i !== activeIdx) return c;
        const areas = [...(c.availableAreas || []), v];
        return {
          ...c,
          availableAreas: areas,
          activeArea: v,
          areasState: {
            ...c.areasState,
            [v]: {
              placesResults: [],
              candidateMap: {},
              dispositions: {},
              isPlacesLoading: false,
              placesError: '',
              discoveredIds: [],
              isApplying: false,
              manualOpen: false,
              manual: { name: '', addressText: '', phone: '', website: '' },
              _autoSearched: true,
              _reviewed: false,
            },
          },
        };
      })
    );
    setNewAreaOpen(false);
    setNewAreaText('');
    setNewStateSelections([]);
  }, [active.availableAreas, active.searchMode, activeIdx, newAreaText, updateActive]);

  const addStates = useCallback(() => {
    const additions = Array.from(new Set(
      (Array.isArray(newStateSelections) ? newStateSelections : [])
        .map((value) => normalizeSearchScopeValue(value, 'state'))
        .filter(Boolean)
    ));
    if (!additions.length) return;

    setEinContexts((prev) =>
      prev.map((c, i) => {
        if (i !== activeIdx) return c;
        const existing = new Set((c.availableAreas || []).map((value) => String(value || '').trim().toUpperCase()));
        const nextAreas = [...(c.availableAreas || [])];
        const nextAreaState = { ...(c.areasState || {}) };

        additions.forEach((code) => {
          if (!existing.has(code)) {
            nextAreas.push(code);
            nextAreaState[code] = nextAreaState[code] || {
              placesResults: [],
              candidateMap: {},
              dispositions: {},
              isPlacesLoading: false,
              placesError: '',
              discoveredIds: [],
              isApplying: false,
              manualOpen: false,
              manual: { name: '', addressText: '', phone: '', website: '' },
              _autoSearched: true,
              _reviewed: false,
            };
          }
        });

        const nextActiveArea = additions.find((code) => !existing.has(code)) || c.activeArea || additions[0];
        return {
          ...c,
          availableAreas: nextAreas,
          activeArea: nextActiveArea,
          areasState: nextAreaState,
        };
      })
    );
    setNewAreaOpen(false);
    setNewStateSelections([]);
    setNewAreaText('');
  }, [activeIdx, newStateSelections]);

  const removeArea = useCallback((areaKey) => {
    setEinContexts((prev) =>
      prev.map((ctx, idx) => {
        if (idx !== activeIdx) return ctx;
        const nextAreas = (ctx.availableAreas || []).filter((value) => String(value) !== String(areaKey));
        const nextAreaState = { ...(ctx.areasState || {}) };
        delete nextAreaState[areaKey];
        const nextLocations = (ctx.storeLocations || []).filter((loc) => String(loc.marketKey || '') !== String(areaKey));
        const nextActiveArea =
          String(ctx.activeArea) === String(areaKey)
            ? nextAreas[0] || ALL_AREAS
            : ctx.activeArea;
        return {
          ...ctx,
          availableAreas: nextAreas,
          areasState: nextAreaState,
          storeLocations: nextLocations,
          activeArea: nextActiveArea,
        };
      })
    );
    setShowSearch(false);
    setNewAreaOpen(false);
    setNewAreaText('');
    setNewStateSelections([]);
    setPendingRemovalArea(null);
  }, [activeIdx]);

  const requestRemoveArea = useCallback((areaKey) => {
    const totalAreas = (active.availableAreas || []).length;
    if (totalAreas <= 1) {
      setPendingRemovalArea(areaKey);
      return;
    }
    removeArea(areaKey);
  }, [active.availableAreas, removeArea]);

  const tabsHasMultiple = einContexts.length > 1;

  // Brand-level tip indicator: default OR any location shows tipEligible
  const einHasTip = active.tipEligible === true || (active.storeLocations || []).some((l) => l.tipEligible === true);

  const reviewedCount = (active.availableAreas || []).filter((mk) => {
    const astate = active.areasState?.[mk];
    const hasLoc = (active.storeLocations || []).some((l) => String(l.marketKey || '') === String(mk));
    return !!(astate?._reviewed || hasLoc);
  }).length;
  const totalAreas = (active.availableAreas || []).length;
  const hasAreas = (active.availableAreas || []).length > 0;

  useEffect(() => {
    setNewStateSelections((prev) =>
      prev.filter((code) => !(active.availableAreas || []).includes(code))
    );
  }, [active.availableAreas]);

  useEffect(() => {
    setPendingRemovalArea(null);
  }, [active.key]);

  return (
    <div style={{ color: 'white' }}>
      {pendingRemovalArea && (
        <div
          style={{
            marginBottom: 16,
            borderRadius: 16,
            border: `1px solid ${PEACH}`,
            background: 'rgba(235,164,119,0.12)',
            padding: '16px 18px',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Remove this search area?</div>
          <div style={{ opacity: 0.9, lineHeight: 1.45 }}>
            Removing the last search area will clear your current candidate locations. You’ll need to add a new search area to continue.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <SmallButton onClick={() => setPendingRemovalArea(null)}>Cancel</SmallButton>
            <ActionButton onClick={() => removeArea(pendingRemovalArea)}>
              Remove and Update Search
            </ActionButton>
          </div>
        </div>
      )}

      <h2 style={{ marginTop: 0, marginBottom: 8 }}>
        Review Candidate Locations
        {einHasTip && <TipEligibleIcon size={18} style={{ marginLeft: 8, transform: 'translateY(-1px)' }} />}
      </h2>
      <p style={{ marginTop: 0, opacity: 0.9 }}>
        We found locations that may belong to this business. Review each result, enroll the correct locations, and exclude any that do not.
      </p>

      {/* Brand tabs */}
      {tabsHasMultiple && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {einContexts.map((ctx) => {
            const ctxHasTip = ctx.tipEligible === true || (ctx.storeLocations || []).some((l) => l.tipEligible === true);
            return (
              <button
                key={ctx.key}
                onClick={() => setActiveKey(String(ctx.key))}
                style={{
                  background: String(active.key) === String(ctx.key) ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
                  border: String(active.key) === String(ctx.key) ? `1px solid ${PEACH}` : '1px solid rgba(255,255,255,0.18)',
                  color: 'white',
                  borderRadius: 12,
                  padding: '8px 12px',
                  cursor: 'pointer',
                }}
              >
                {ctx.label}
                {ctxHasTip && <TipEligibleIcon style={{ marginLeft: 6 }} />}
                {(ctx.storeLocations?.length || 0) ? ` • ${ctx.storeLocations.length}` : ''}
              </button>
            );
          })}
        </div>
      )}

      {/* Online-only badge */}
      {active.isOnlineOnly && (
        <div style={{ marginBottom: 10 }}>
          <span style={pill(PEACH)}>Online-only (no locations to add)</span>
        </div>
      )}

      {/* Area selector & summary */}
      {!active.isOnlineOnly && (
        <>
          {/* Search area chips */}
          <div style={{ margin: '6px 0 12px' }}>
            <div style={{ fontWeight: 600, opacity: 0.95, marginBottom: 6 }}>
              Search Areas {active.searchMode ? `(${SEARCH_GRANULARITY_LABELS[active.searchMode] || 'Area'})` : ''}
            </div>
            {hasAreas ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={() => updateActive({ activeArea: ALL_AREAS })}
                  style={{
                    background:
                      active.activeArea === ALL_AREAS || !active.activeArea ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
                    border:
                      active.activeArea === ALL_AREAS || !active.activeArea ? `1px solid ${PEACH}` : '1px solid rgba(255,255,255,0.18)',
                    color: 'white',
                    borderRadius: 12,
                    padding: '6px 10px',
                    cursor: 'pointer',
                  }}
                >
                  All
                  {(active.storeLocations?.length || 0) ? ` • ${active.storeLocations.length}` : ''}
                </button>

                {(active.availableAreas || []).map((mk) => (
                  <div
                    key={mk}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: String(active.activeArea) === String(mk) ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
                      border: String(active.activeArea) === String(mk) ? `1px solid ${PEACH}` : '1px solid rgba(255,255,255,0.18)',
                      color: 'white',
                      borderRadius: 12,
                      padding: '6px 10px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => updateActive({ activeArea: mk })}
                      style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0 }}
                    >
                      {mk}
                      {areaSummary[mk]?.enrolled ? ` • ${areaSummary[mk].enrolled}` : ''}
                    </button>
                    <button
                      type="button"
                      onClick={() => requestRemoveArea(mk)}
                      style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, opacity: 0.85 }}
                      aria-label={`Remove ${mk}`}
                    >
                      ×
                    </button>
                  </div>
                ))}

                <button
                  onClick={() => setNewAreaOpen((v) => !v)}
                  style={{
                    background: 'rgba(255,255,255,0.10)',
                    border: '1px dashed rgba(255,255,255,0.35)',
                    color: 'white',
                    borderRadius: 12,
                    padding: '6px 10px',
                    cursor: 'pointer',
                  }}
                >
                  + Add Search Area
                </button>

                {newAreaOpen && (
                  <Step3AreaPicker
                    mode={active.searchMode}
                    values={active.availableAreas || []}
                    textValue={newAreaText}
                    onTextChange={setNewAreaText}
                    stateSelections={newStateSelections}
                    onStateSelectionsChange={setNewStateSelections}
                    onAddText={addArea}
                    onAddStates={addStates}
                    onCancel={() => {
                      setNewAreaOpen(false);
                      setNewAreaText('');
                      setNewStateSelections([]);
                    }}
                  />
                )}
              </div>
            ) : null}
          </div>

          {/* Search area summary */}
          {hasAreas && (
            <div
              style={{
                marginTop: 8,
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid rgba(255,255,255,0.10)',
                  fontWeight: 600,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>Search Area Summary — {active.label}</span>
                {totalAreas > 0 && reviewedCount < totalAreas && (
                  <span style={{ fontWeight: 500, fontSize: 12, opacity: 0.9 }}>
                    Review all search areas: {reviewedCount}/{totalAreas}
                  </span>
                )}
              </div>
              <div style={{ padding: '8px 12px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    gap: 8,
                    opacity: 0.9,
                    fontSize: 13,
                    marginBottom: 6,
                  }}
                >
                  <div>Area</div>
                  <div style={{ textAlign: 'right' }}>Enrolled</div>
                  <div style={{ textAlign: 'right' }}>Expected</div>
                  <div style={{ textAlign: 'right' }}>Status</div>
                </div>
                {(active.availableAreas || []).map((mk) => {
                  const { enrolledCount, expectedCount, coveragePct } = calcCoverage(active, mk);
                  const status =
                    expectedCount === 0 && enrolledCount === 0
                      ? { label: 'No search yet', bg: 'rgba(255,255,255,0.28)' }
                      : enrolledCount >= expectedCount && expectedCount > 0
                      ? { label: 'Complete', bg: GREEN }
                      : { label: `Pending (${Math.max(0, expectedCount - enrolledCount)} left)`, bg: '#F59E0B' };
                  return (
                    <div
                      key={`row-${mk}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        gap: 8,
                        padding: '6px 0',
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mk}</div>
                      <div style={{ textAlign: 'right' }}>{enrolledCount}</div>
                      <div style={{ textAlign: 'right' }}>{expectedCount}</div>
                      <div style={{ textAlign: 'right' }}>
                        <span
                          style={{
                            borderRadius: 999,
                            padding: '2px 8px',
                            fontSize: 12,
                            border: '1px solid rgba(255,255,255,0.25)',
                            background: status.bg,
                          }}
                        >
                          {status.label}
                          {expectedCount > 0 ? ` • ${coveragePct}%` : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!hasAreas && (
            <div
              style={{
                marginTop: 8,
                borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.06)',
                padding: '18px',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No search areas selected</div>
              <div style={{ opacity: 0.88, marginBottom: 14, lineHeight: 1.45 }}>
                Add at least one search area to continue finding this business’s locations.
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 600, opacity: 0.95, marginBottom: 8 }}>Search Granularity</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {SEARCH_GRANULARITY_OPTIONS.map((option) => {
                    const isActive = (active.searchMode || 'area') === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          updateActive({ searchMode: option.value, activeArea: '' });
                          setNewAreaText('');
                          setNewStateSelections([]);
                        }}
                        style={{
                          background: isActive ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
                          border: isActive ? `1px solid ${PEACH}` : '1px solid rgba(255,255,255,0.18)',
                          color: 'white',
                          borderRadius: 12,
                          padding: '8px 12px',
                          cursor: 'pointer',
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Step3AreaPicker
                mode={active.searchMode || 'area'}
                values={active.availableAreas || []}
                textValue={newAreaText}
                onTextChange={setNewAreaText}
                stateSelections={newStateSelections}
                onStateSelectionsChange={setNewStateSelections}
                onAddText={addArea}
                onAddStates={addStates}
                onCancel={() => {
                  setNewAreaOpen(false);
                  setNewAreaText('');
                  setNewStateSelections([]);
                }}
                alwaysOpen
              />
            </div>
          )}
        </>
      )}

      {/* Action row */}
      {!active.isOnlineOnly && hasAreas && active.activeArea && active.activeArea !== ALL_AREAS && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          <ActionButton
            onClick={() => {
              if (!showSearch) {
                setShowSearch(true);
                setTimeout(() => performPlacesSearch(false), 0);
              } else {
                setShowSearch(false);
              }
            }}
            style={{
              background: showSearch ? PEACH : GREEN,
              border: showSearch ? `1px solid ${PEACH}` : `1px solid ${GREEN}`,
              color: showSearch ? '#1a1a1a' : '#fff',
            }}
          >
            {showSearch ? 'Hide Search' : 'Refine Search'}
          </ActionButton>
          <ActionButton
            onClick={applyDispositions}
            disabled={
              getAreaState(active, active.activeArea)?.isApplying ||
              !Object.keys(getAreaState(active, active.activeArea)?.dispositions || {}).length
            }
            style={{ opacity: getAreaState(active, active.activeArea)?.isApplying ? 0.7 : 1 }}
          >
            {getAreaState(active, active.activeArea)?.isApplying ? 'Confirming…' : 'Confirm Selections'}
          </ActionButton>
          {unmetRequiredIds.length > 0 && (
            <div style={{ color: '#ffdada', marginLeft: 8 }}>
              {unmetRequiredIds.length} required candidate{unmetRequiredIds.length > 1 ? 's' : ''} not yet enrolled or excluded.
            </div>
          )}
        </div>
      )}

      {/* Refine search panel */}
      {!active.isOnlineOnly && hasAreas && active.activeArea && active.activeArea !== ALL_AREAS && showSearch && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 12,
              alignItems: 'end',
              marginTop: 12,
            }}
          >
            <div>
              <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Search Name</label>
              <FrostedInput
                placeholder="Public-facing business name"
                value={active.searchName ?? active.searchNameDefault ?? ''}
                onChange={(e) => updateActive({ searchName: e.target.value })}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>
                {SEARCH_GRANULARITY_LABELS[active.searchMode] || 'Search Area'} Values
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {(active.availableAreas || []).map((mk) => (
                  <span
                    key={`refine-${mk}`}
                    style={{
                      border: '1px solid rgba(255,255,255,0.25)',
                      padding: '4px 8px',
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.06)',
                    }}
                  >
                    {mk}
                    <button
                      type="button"
                      onClick={() => requestRemoveArea(mk)}
                      style={{ marginLeft: 6, background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <SmallButton onClick={() => setNewAreaOpen(true)}>
                  Add {SEARCH_GRANULARITY_LABELS[active.searchMode] || 'Area'}
                </SmallButton>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <SmallButton onClick={() => performPlacesSearch(false)} disabled={area.isPlacesLoading}>
              {area.isPlacesLoading ? 'Searching…' : 'Run Search'}
            </SmallButton>
            {area.placesError && <div style={{ marginTop: 10, color: '#ffdada' }}>{area.placesError}</div>}
          </div>
        </>
      )}

      {/* Results */}
      {!active.isOnlineOnly &&
        hasAreas &&
        active.activeArea &&
        active.activeArea !== ALL_AREAS &&
        visibleCandidates.length > 0 && (
          <div
            style={{
              marginTop: 14,
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '10px 14px',
                borderBottom: '1px solid rgba(255,255,255,0.10)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ fontWeight: 600 }}>Candidates — {active.activeArea}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <SmallButton
                  onClick={() => {
                    const d = { ...(area.dispositions || {}) };
                    visibleCandidates.forEach((r) => {
                      d[r.place_id] = { action: 'enroll' };
                    });
                    setAreaState(active.activeArea, { dispositions: d, _reviewed: true });
                  }}
                >
                  Mark all Enroll
                </SmallButton>
                <SmallButton onClick={() => setAreaState(active.activeArea, { dispositions: {} })}>Clear actions</SmallButton>
              </div>
            </div>

            {visibleCandidates.map((r) => {
              const disp = (area.dispositions || {})[r.place_id] || { action: '' };
              const icon = r.iconMaskBaseUri;
              const bg = r.iconBackgroundColor || '#ffffff';
              const excluded = disp.action === 'exclude';
              return (
                <div
                  key={r.place_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr 280px',
                    gap: 12,
                    alignItems: 'start',
                    padding: '12px 14px',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div
                    style={{
                      height: 32,
                      width: 32,
                      borderRadius: 8,
                      background: bg,
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    {icon ? <img src={`${icon}.png`} alt="" style={{ height: 20, width: 20 }} /> : '🏷️'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.displayName}</div>
                    <div style={{ opacity: 0.85 }}>{r.formatted_address}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      {r.primaryType && <span style={chipStyle}>{r.primaryType}</span>}
                      {r.businessStatus && <span style={chipStyle}>Status: {r.businessStatus}</span>}
                    </div>
                  </div>

                  {/* Disposition controls */}
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                      <select
                        className="frosted-input"
                        value={disp.action}
                        onChange={(e) => {
                          const action = e.target.value;
                          setAreaState(active.activeArea, (prevAreaState) => {
                            const d = { ...(prevAreaState.dispositions || {}) };
                            d[r.place_id] = { action };
                            if (action !== 'exclude') {
                              delete d[r.place_id].reason;
                              delete d[r.place_id].reopenDate;
                            }
                            return { dispositions: d, _reviewed: true };
                          });
                        }}
                        style={{
                          width: '100%',
                          height: 48,
                          borderRadius: 12,
                          border: '1px solid rgba(255,255,255,0.10)',
                          background: 'rgba(255,255,255,0.12)',
                          color: 'white',
                          fontSize: 16,
                          padding: '0 12px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      >
                        <option value="">Choose action…</option>
                        <option value="enroll" style={{ color: '#000' }}>
                          Enroll
                        </option>
                        <option value="exclude" style={{ color: '#000' }}>
                          Temporarily Exclude
                        </option>
                        <option value="not_mine" style={{ color: '#000' }}>
                          Not My Store
                        </option>
                      </select>

                      {excluded && (
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
                          <select
                            className="frosted-input"
                            value={disp.reason || ''}
                            onChange={(e) => {
                              const reason = e.target.value;
                              setAreaState(active.activeArea, (prevAreaState) => {
                                const d = { ...(prevAreaState.dispositions || {}) };
                                d[r.place_id] = { ...(d[r.place_id] || {}), action: 'exclude', reason };
                                return { dispositions: d, _reviewed: true };
                              });
                            }}
                            style={{
                              width: '100%',
                              height: 48,
                              borderRadius: 12,
                              border: '1px solid rgba(255,255,255,0.10)',
                              background: 'rgba(255,255,255,0.12)',
                              color: 'white',
                              fontSize: 16,
                              padding: '0 12px',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          >
                            <option value="">Reason…</option>
                            {TEMP_EXCLUDE_REASONS.map((x) => (
                              <option key={x} value={x} style={{ color: '#000' }}>
                                {x}
                              </option>
                            ))}
                          </select>
                          <input
                            type="date"
                            className="frosted-input"
                            value={disp.reopenDate || ''}
                            onChange={(e) => {
                              const reopenDate = e.target.value;
                              setAreaState(active.activeArea, (prevAreaState) => {
                                const d = { ...(prevAreaState.dispositions || {}) };
                                d[r.place_id] = { ...(d[r.place_id] || {}), action: 'exclude', reopenDate };
                                return { dispositions: d, _reviewed: true };
                              });
                            }}
                            style={{
                              width: '100%',
                              height: 48,
                              borderRadius: 12,
                              border: '1px solid rgba(255,255,255,0.10)',
                              background: 'rgba(255,255,255,0.12)',
                              color: 'white',
                              fontSize: 16,
                              padding: '0 12px',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Manual add */}
      {!active.isOnlineOnly && hasAreas && active.activeArea && active.activeArea !== ALL_AREAS && (
        <div style={{ marginTop: 22 }}>
          <SmallButton onClick={() => setAreaState(active.activeArea, { manualOpen: !area.manualOpen })}>
            {area.manualOpen ? 'Hide manual add' : 'Add manually'}
          </SmallButton>
          {area.manualOpen && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 12, marginTop: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Location Name</label>
                  <FrostedInput
                    placeholder="e.g., rypil – West Loop"
                    value={area.manual?.name || ''}
                    onChange={(e) => setAreaState(active.activeArea, { manual: { ...area.manual, name: e.target.value } })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Address</label>
                  <FrostedInput
                    placeholder="e.g., 123 Main St, Chicago, IL 60607"
                    value={area.manual?.addressText || ''}
                    onChange={(e) =>
                      setAreaState(active.activeArea, { manual: { ...area.manual, addressText: e.target.value } })
                    }
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Phone</label>
                  <FrostedInput
                    placeholder="(optional)"
                    value={area.manual?.phone || ''}
                    onChange={(e) => setAreaState(active.activeArea, { manual: { ...area.manual, phone: e.target.value } })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Website</label>
                  <FrostedInput
                    placeholder="(optional)"
                    value={area.manual?.website || ''}
                    onChange={(e) => setAreaState(active.activeArea, { manual: { ...area.manual, website: e.target.value } })}
                  />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <SmallButton onClick={addManualLocation}>Add Manual Location</SmallButton>
              </div>
            </>
          )}
        </div>
      )}

      {/* Added Locations */}
      {!active.isOnlineOnly && (active.storeLocations || []).length > 0 && (
        <div
          style={{
            marginTop: 22,
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ fontWeight: 600 }}>
              Added Locations{' '}
              {active.activeArea && active.activeArea !== ALL_AREAS
                ? `(${visibleLocs.length} in ${active.activeArea})`
                : `(${active.storeLocations.length})`}
            </div>
          </div>

          {visibleLocs.map(({ loc, globalIdx }) => {
            const excluded = loc.status === 'temp_excluded';
            const isGoogle = loc.placeSource === 'google';
            const iconUrl = loc.iconMaskBaseUri ? `${loc.iconMaskBaseUri}.png` : '';

            return (
              <div
                key={`${loc.placeId || 'manual'}-${globalIdx}-${loc.marketKey || ''}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 12,
                  padding: '12px 14px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {iconUrl ? (
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          background: loc.iconBackgroundColor || 'rgba(255,255,255,0.18)',
                          display: 'grid',
                          placeItems: 'center',
                          overflow: 'hidden',
                          border: '1px solid rgba(255,255,255,0.25)',
                        }}
                      >
                        <img src={iconUrl} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                      </div>
                    ) : null}
                    <div style={{ fontWeight: 700 }}>
                      {loc.name || '—'}{' '}
                      <span style={{ opacity: 0.8, fontWeight: 400 }}>
                        {isGoogle ? '(Google)' : '(Manual)'} • {loc.marketKey || 'Area'}
                      </span>
                      {loc.tipEligible === true && <TipEligibleIcon size={16} style={{ marginLeft: 6 }} />}
                      {excluded && (
                        <span style={{ marginLeft: 8, color: PEACH, fontWeight: 500 }}>• Temporarily Excluded</span>
                      )}
                    </div>
                  </div>

                  {loc.addressText ? <div style={{ opacity: 0.9, marginTop: 2 }}>{loc.addressText}</div> : null}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {loc.primaryType ? <span style={chipStyle}>{loc.primaryType}</span> : null}
                    {loc.businessStatus ? <span style={chipStyle}>Status: {loc.businessStatus}</span> : null}
                    {loc.mcc ? <span style={chipStyle}>MCC: {loc.mcc}</span> : null}
                  </div>

                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
                    {loc.phone ? (
                      <a href={`tel:${loc.phone}`} style={linkStyle}>
                        {loc.phone}
                      </a>
                    ) : null}
                    {loc.website ? (
                      <a href={loc.website} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        {prettyUrl(loc.website)}
                      </a>
                    ) : null}
                  </div>

                  {Array.isArray(loc.hoursText) && loc.hoursText.length > 0 && (
                    <div
                      style={{
                        marginTop: 10,
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.08)',
                        borderRadius: 14,
                        padding: '12px 14px',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                      }}
                    >
                      {loc.hoursText.join('\n')}
                    </div>
                  )}

                  {/* Temp exclusion controls */}
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={excluded}
                        onChange={(e) => {
                          const status = e.target.checked ? 'temp_excluded' : 'active';
                          const next = active.storeLocations.map((L, i) =>
                            i === globalIdx ? { ...L, status, ...(status === 'active' ? { exclusionReason: '' } : {}) } : L
                          );
                          updateActive({ storeLocations: next });
                        }}
                        style={{ width: 18, height: 18, accentColor: PEACH }}
                      />
                      <span>Temporarily exclude this location</span>
                    </label>

                    {excluded && (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginTop: 10 }}>
                          <select
                            className="frosted-input"
                            value={loc.exclusionReason || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const next = active.storeLocations.map((L, i) => (i === globalIdx ? { ...L, exclusionReason: val } : L));
                              updateActive({ storeLocations: next });
                            }}
                            style={{
                              width: '100%',
                              height: 56,
                              borderRadius: 14,
                              border: '1px solid rgba(255,255,255,0.10)',
                              background: 'rgba(255,255,255,0.12)',
                              color: 'white',
                              fontSize: 16,
                              padding: '0 12px',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          >
                            <option value="">Select a reason…</option>
                            {TEMP_EXCLUDE_REASONS.map((r) => (
                              <option key={r} value={r} style={{ color: '#000' }}>
                                {r}
                              </option>
                            ))}
                          </select>

                          <input
                            type="date"
                            className="frosted-input"
                            value={loc.reopenDate || ''}
                            onChange={(e) => {
                              const next = active.storeLocations.map((L, i) =>
                                i === globalIdx ? { ...L, reopenDate: e.target.value } : L
                              );
                              updateActive({ storeLocations: next });
                            }}
                            style={{
                              width: '100%',
                              height: 56,
                              borderRadius: 14,
                              border: '1px solid rgba(255,255,255,0.10)',
                              background: 'rgba(255,255,255,0.12)',
                              color: 'white',
                              fontSize: 16,
                              padding: '0 12px',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        {(!loc.exclusionReason || !loc.exclusionReason.trim() || !loc.reopenDate || !loc.reopenDate.trim()) && (
                          <div style={{ marginTop: 6, color: '#ffdada' }}>
                            A reason <em>and</em> reopen date are required while excluded.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <SmallButton
                    onClick={() => {
                      const next = (active.storeLocations || []).filter((_, i) => i !== globalIdx);
                      updateActive({ storeLocations: next });
                    }}
                  >
                    Remove
                  </SmallButton>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Back link */}
      {!isFirstStep && (
        <button
          type="button"
          onClick={() => onPrevious?.(buildStep3Payload(einRef.current))}
          style={{
            marginTop: 20,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.9)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      )}

      <style>{`.frosted-input::placeholder{color:rgba(255,255,255,0.75);}`}</style>
    </div>
  );
}

function Step3AreaPicker({
  mode,
  values = [],
  textValue = '',
  onTextChange,
  stateSelections = [],
  onStateSelectionsChange,
  onAddText,
  onAddStates,
  onCancel,
  alwaysOpen = false,
}) {
  if (mode === 'state') {
    return (
      <div style={{ display: 'grid', gap: 8, minWidth: 320, width: '100%', maxWidth: 520 }}>
        <div style={{ opacity: 0.9, fontSize: 14 }}>
          Add one or more states where this business operates physical locations.
        </div>
        <Step3StatePicker
          values={values}
          selections={stateSelections}
          onSelectionsChange={onStateSelectionsChange}
          onAdd={onAddStates}
        />
        {!alwaysOpen && (
          <div>
            <SmallButton onClick={onCancel}>Cancel</SmallButton>
          </div>
        )}
      </div>
    );
  }

  const isZip = mode === 'zip';
  const helperText = isZip
    ? 'Add the ZIP codes where this business operates physical locations.'
    : 'Add cities in City, ST format so we can match the right location.';
  const placeholder = isZip ? 'Enter a ZIP code' : 'Enter a city and state, like Lake Forest, IL';
  const microcopy = isZip
    ? 'Add one ZIP code at a time, then click Add.'
    : 'Add one city at a time, then click Add. Examples: Lake Forest, IL; Lake Forest, CA.';
  const normalizedText = String(textValue || '');
  const isDisabled = isZip ? !/^\d{5}$/.test(normalizedText.trim()) : !normalizedText.trim();

  return (
    <div style={{ display: 'grid', gap: 8, minWidth: 320, width: '100%', maxWidth: 560 }}>
      <div style={{ opacity: 0.9, fontSize: 14 }}>{helperText}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'start' }}>
        <FrostedInput
          value={textValue}
          onChange={(e) =>
            onTextChange?.(
              isZip ? e.target.value.replace(/\D+/g, '').slice(0, 5) : e.target.value
            )
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isDisabled) {
              e.preventDefault();
              onAddText?.();
            }
          }}
          placeholder={placeholder}
          style={{ height: 44, fontSize: 16, borderRadius: 12 }}
        />
        <ActionButton
          type="button"
          onClick={() => onAddText?.()}
          disabled={isDisabled}
          style={{
            height: 44,
            padding: '0 14px',
            opacity: isDisabled ? 0.55 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          Add
        </ActionButton>
        {!alwaysOpen && (
          <SmallButton type="button" onClick={onCancel} style={{ height: 44 }}>
            Cancel
          </SmallButton>
        )}
      </div>
      <div style={{ opacity: 0.75, fontSize: 13 }}>{microcopy}</div>
    </div>
  );
}

function Step3StatePicker({
  values = [],
  selections = [],
  onSelectionsChange,
  onAdd,
}) {
  const [query, setQuery] = useState('');
  const filteredStates = STATE_OPTIONS.filter(([code, label]) => {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return true;
    return code.toLowerCase().includes(needle) || label.toLowerCase().includes(needle);
  });
  const canAdd = selections.length > 0;

  const toggleSelection = (code) => {
    if (values.includes(code)) return;
    onSelectionsChange?.(
      selections.includes(code)
        ? selections.filter((value) => value !== code)
        : [...selections, code]
    );
  };

  return (
    <div
      style={{
        borderRadius: 16,
        border: '1px solid rgba(255,255,255,0.18)',
        background: 'rgba(22, 54, 61, 0.92)',
        padding: 10,
      }}
    >
      <FrostedInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Choose a state"
        style={{ height: 48, fontSize: 16, marginBottom: 8 }}
      />
      <div
        style={{
          maxHeight: 180,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          paddingRight: 4,
        }}
      >
        {filteredStates.length ? (
          filteredStates.map(([code, label]) => {
            const isAdded = values.includes(code);
            const isSelected = selections.includes(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => toggleSelection(code)}
                style={{
                  width: '100%',
                  borderRadius: 12,
                  border: `1px solid ${(isSelected || isAdded) ? PEACH : 'rgba(255,255,255,0.12)'}`,
                  background: isAdded
                    ? 'rgba(255,255,255,0.12)'
                    : isSelected
                    ? 'rgba(235,164,119,0.18)'
                    : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  padding: '10px 12px',
                  textAlign: 'left',
                  cursor: isAdded ? 'default' : 'pointer',
                  fontSize: 15,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: isAdded ? 0.7 : 1,
                }}
              >
                <span>{label} ({code})</span>
                {(isSelected || isAdded) && (
                  <span style={{ fontSize: 13, opacity: 0.9 }}>
                    {isAdded ? 'Added' : 'Selected'}
                  </span>
                )}
              </button>
            );
          })
        ) : (
          <div
            style={{
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.78)',
              padding: '12px 14px',
              fontSize: 14,
            }}
          >
            No matching states.
          </div>
        )}
      </div>

      {!!selections.length && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {selections.map((code) => (
            <span
              key={code}
              style={{
                background: 'rgba(235,164,119,0.18)',
                border: `1px solid ${PEACH}`,
                borderRadius: 12,
                padding: '6px 10px',
              }}
            >
              {STATE_LABEL_BY_CODE[code] || code}
              <button
                type="button"
                onClick={() => onSelectionsChange?.(selections.filter((value) => value !== code))}
                style={{
                  marginLeft: 6,
                  background: 'transparent',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                }}
                aria-label={`Remove ${STATE_LABEL_BY_CODE[code] || code}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
        <ActionButton
          type="button"
          onClick={() => onAdd?.()}
          disabled={!canAdd}
          style={{
            opacity: canAdd ? 1 : 0.55,
            cursor: canAdd ? 'pointer' : 'not-allowed',
          }}
        >
          Add
        </ActionButton>
        <div style={{ opacity: 0.75, fontSize: 13 }}>
          Click states to select or deselect them, then click Add to add them all at once.
        </div>
      </div>
    </div>
  );
}

/* ---------- styles ---------- */
const chipStyle = {
  border: '1px solid rgba(255,255,255,0.25)',
  padding: '4px 8px',
  borderRadius: 10,
  background: 'rgba(255,255,255,0.06)',
  fontSize: 13,
};

const linkStyle = {
  color: 'white',
  textDecoration: 'underline',
  opacity: 0.95,
};

function pill(bg) {
  return {
    display: 'inline-block',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 12,
    border: '1px solid rgba(255,255,255,0.25)',
    background: bg,
  };
}
